/**
 * The response chart's viewport: how much history there is to scroll, how
 * wide a window may be, how + / - and a pinch change it, and how a native
 * horizontal scroll maps to time. Pure: no React, no DOM. The data drawn in
 * the window (steps, tiles, rows, lane, stats) is `monitoringSeries.ts`, which
 * re-exports this file.
 *
 * The rules, and how each goes quietly wrong without a test:
 *
 *  - A site added ten minutes ago still gets a frame. The history is never
 *    shorter than the narrowest zoom, and a window wider than the history
 *    keeps now at the right edge: the newest check is the one being awaited.
 *  - A zoom keeps the time under the pointer (or between the fingers) where
 *    it is, and is then pushed back inside the history, so zooming out at
 *    either end never shows time that has no checks.
 *  - + and - walk a fixed ladder from wherever a pinch left the span, and a
 *    span a hair off a rung counts as that rung, so a press always shows. A
 *    rung the history's length clamps to a hair off the current span is no
 *    rung at all, so the button is disabled instead of doing nothing visible.
 *  - The virtual scroll's ends are exact: scrolled fully right is now, fully
 *    left is the oldest kept check, whatever rounding the browser does.
 */

// ── Time and zoom ────────────────────────────────────────────────────────────

export const MINUTE_MS = 60_000;
export const HOUR_MS = 3_600_000;
export const DAY_MS = 86_400_000;
/** Raw checks are kept this long on the server (rollup-run.ts KEEP_CHECKS_MS). */
export const CHECKS_KEPT_MS = 45 * DAY_MS;
/** The +/- ladder, narrowest first. */
export const ZOOM_SPANS_MS: readonly number[] = [
  30 * MINUTE_MS,
  HOUR_MS,
  3 * HOUR_MS,
  6 * HOUR_MS,
  12 * HOUR_MS,
  DAY_MS,
  3 * DAY_MS,
  7 * DAY_MS,
  14 * DAY_MS,
  30 * DAY_MS,
  45 * DAY_MS,
];
export const MIN_SPAN_MS = 30 * MINUTE_MS;
export const DEFAULT_SPAN_MS = DAY_MS;

/** The whole scrollable history, epoch ms. */
export interface TimeRange {
  start: number;
  end: number;
}

/** What is on screen: [from, from + span), epoch ms. */
export interface TimeView {
  from: number;
  span: number;
}

// ── History, zoom and scroll ─────────────────────────────────────────────────

/**
 * The scrollable history: start = max(historyFrom, now - CHECKS_KEPT_MS)
 * (now - DEFAULT_SPAN_MS when null), end = now; always at least MIN_SPAN_MS
 * long, so a site added ten minutes ago still gets a frame to draw in.
 */
export function historyRange(historyFrom: string | null, now: number): TimeRange {
  const oldest = historyFrom ? Date.parse(historyFrom) : Number.NaN;
  const start = Number.isFinite(oldest) ? Math.max(oldest, now - CHECKS_KEPT_MS) : now - DEFAULT_SPAN_MS;
  return { start: Math.min(start, now - MIN_SPAN_MS), end: now };
}

/** Span clamped to [MIN_SPAN_MS, min(CHECKS_KEPT_MS, range length)] (never below MIN_SPAN_MS). */
export function clampSpan(span: number, range: TimeRange): number {
  const widest = Math.max(MIN_SPAN_MS, Math.min(CHECKS_KEPT_MS, range.end - range.start));
  const want = Number.isFinite(span) ? span : DEFAULT_SPAN_MS;
  return Math.min(widest, Math.max(MIN_SPAN_MS, want));
}

/**
 * from clamped so [from, from + span) stays inside range. A span wider than
 * the whole history (only a site younger than MIN_SPAN_MS has one) keeps now
 * at the right edge, because the newest check is the one being waited for.
 */
export function clampFrom(from: number, span: number, range: TimeRange): number {
  const last = range.end - span;
  if (last <= range.start || !Number.isFinite(from)) return last;
  return Math.min(last, Math.max(range.start, from));
}

/** Within this share a span counts as the ladder's own: a pinch that ends at 24 h 10 min
 *  and a press of "-" should give 3 days, not 24 h. */
const SAME_SPAN = 0.01;

/**
 * The next ladder span strictly narrower (dir -1, zoom in) or wider (dir +1,
 * zoom out), clamped; the same span when there is none, which is how the
 * buttons know to be disabled. Works from any current span (after a pinch it
 * is off the ladder).
 *
 * A rung the history clamps back to within SAME_SPAN of the current span is
 * no step either: a history of 24 h 10 min at 24 h would otherwise offer one
 * zoom out that moves the plot by under a percent and still reads "24 h".
 */
export function nextZoomSpan(span: number, dir: 1 | -1, range: TimeRange): number {
  const current = clampSpan(span, range);
  const next =
    dir < 0
      ? [...ZOOM_SPANS_MS].reverse().find((s) => s < current * (1 - SAME_SPAN))
      : ZOOM_SPANS_MS.find((s) => s > current * (1 + SAME_SPAN));
  if (next === undefined) return current;
  const out = clampSpan(next, range);
  return Math.abs(out - current) <= current * SAME_SPAN ? current : out;
}

/** Keep the time under `anchor` (0..1 across the plot) where it is while the span changes; clamped. */
export function zoomAround(view: TimeView, newSpan: number, anchor: number, range: TimeRange): TimeView {
  const a = Number.isFinite(anchor) ? Math.min(1, Math.max(0, anchor)) : 0.5;
  const at = view.from + a * view.span;
  const span = clampSpan(newSpan, range);
  return { from: clampFrom(at - a * span, span, range), span };
}

/**
 * Virtual scroll: the content is wide enough that scrollLeft 0 shows
 * range.start and the maximum shows range.end. Only the visible window is
 * ever drawn; the spacer just gives the browser a native scrollbar, momentum
 * and shift+wheel. 45 days at a 30-minute span on a 1000 px plot is about
 * 2.2 million px, inside every browser's element size limit.
 */
export interface ScrollGeometry {
  msPerPx: number;
  contentWidth: number;
  maxScroll: number;
}

/**
 * The scroll distance is whole pixels (a browser reports scrollWidth rounded),
 * and msPerPx is then taken from it rather than from the plot width, so the
 * two ends map to range.start and range.end exactly. The difference from
 * span / plotWidth is under half a pixel across the whole history.
 */
export function scrollGeometry(range: TimeRange, span: number, viewportWidth: number, plotWidth: number): ScrollGeometry {
  const perPx = span / Math.max(1, plotWidth);
  const travel = Math.max(0, range.end - range.start - span);
  const maxScroll = Math.round(travel / perPx);
  const msPerPx = maxScroll > 0 ? travel / maxScroll : perPx;
  return { msPerPx, contentWidth: Math.max(0, viewportWidth) + maxScroll, maxScroll };
}

export function fromOfScroll(scrollLeft: number, range: TimeRange, geo: ScrollGeometry): number {
  const px = Math.min(geo.maxScroll, Math.max(0, Number.isFinite(scrollLeft) ? scrollLeft : 0));
  return range.start + px * geo.msPerPx;
}

export function scrollOfFrom(from: number, range: TimeRange, geo: ScrollGeometry): number {
  if (!(geo.msPerPx > 0)) return 0;
  return Math.min(geo.maxScroll, Math.max(0, (from - range.start) / geo.msPerPx));
}

// ── Labels ───────────────────────────────────────────────────────────────────

/**
 * "30 min", "6 h", "3 p" (Estonian day unit comes from the caller). A ladder
 * span reads in one unit; up to two days reads in hours, as the ladder's
 * "24 h" does. A span a pinch left off the ladder gets a second unit while it
 * is short enough for it to matter ("1 h 20 min", "4 p 6 h").
 */
export function formatSpan(span: number, units: { d: string; h: string; min: string }): string {
  const minutes = Math.max(1, Math.round(span / MINUTE_MS));
  if (minutes < 60) return `${minutes} ${units.min}`;
  if (minutes < 48 * 60) {
    const h = Math.floor(minutes / 60);
    const rest = minutes % 60;
    if (rest === 0 || h >= 6) return `${Math.round(minutes / 60)} ${units.h}`;
    return `${h} ${units.h} ${rest} ${units.min}`;
  }
  const hours = Math.round(minutes / 60);
  const d = Math.floor(hours / 24);
  const rest = hours % 24;
  if (rest === 0 || d >= 7) return `${Math.round(hours / 24)} ${units.d}`;
  return `${d} ${units.d} ${rest} ${units.h}`;
}

/** Which tick shape the x axis uses: "time" (HH:mm) up to 36 h, "dayTime" up to 4 days, "day" (d.M) above. */
export function tickShape(span: number): "time" | "dayTime" | "day" {
  if (span <= 36 * HOUR_MS) return "time";
  if (span <= 4 * DAY_MS) return "dayTime";
  return "day";
}
